/**
 * 認証が確かめられた瞬間の analytics 配線。
 *
 * やることは2つ。PostHog 側で identify (anonymous distinct_id を本人に
 * 結ぶ) し、api-go 側で /api/learning-events/claim (同じ anonymousId の
 * 出来事へ user_id を後付け) する。どちらも観測用の記録で、
 * **ログインの道を止めない・遅くしない**ために全部 fire-and-forget。
 */
import { readFirstTouch } from "./first-touch.client";
import {
  identifyPostHogUserForSession,
  resetPostHogUser,
  type PostHogSessionIdentity,
} from "./posthog.client";

/**
 * 同じタブ内で同じ組を二度名乗らないための記録。
 * サーバー側の claim は冪等 (claims は足すだけ・user_id の後付けは
 * 持ち主の無い行だけ) なので、これは往復を減らすだけの目印。
 */
const CLAIMED_SESSION_KEY = "yumesaku:learning-claim:v1";

/**
 * signup の action が通った印。登録だけではセッションが無い
 * (email 確認が済むまでログイン不可) ので、完了の記録は「最初に
 * 本人が確かめられた瞬間」まで持ち越す。別タブ・別日の確認でも
 * 拾えるよう localStorage。無期限に残ると次の名乗りで古い登録を
 * 再報告するので期限を設ける
 */
const SIGNUP_PENDING_KEY = "yumesaku:signup-pending:v1";
const SIGNUP_PENDING_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * 認証の往復を待たずに届けるための送信。遷移やタブを閉じる途中でも
 * 残る sendBeacon を先に使い、無ければ keepalive の fetch に落とす。
 * 応答は読まない (名乗り・記録は結果を待つ仕事ではない)
 */
function postTelemetryJson(path: string, body: unknown): void {
  const payload = JSON.stringify(body);
  try {
    if (typeof navigator?.sendBeacon === "function") {
      const queued = navigator.sendBeacon(
        path,
        new Blob([payload], { type: "application/json" }),
      );
      if (queued) return;
    }
  } catch {
    // beacon が使えない/失敗したときは fetch 側へ
  }
  try {
    void fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // 記録のために画面の仕事を止めない
  }
}

/**
 * anonymousId → user の名乗り。1 セッションに同じ組で 1 度だけ。
 * 共有PCで別人がログインし直した場合 (user が変わった) は別の組になる
 * ので再度名乗る — サーバー側は既に持ち主のある行を動かさない
 */
function claimAnonymousIdOnce(userId: string, anonymousId: string): void {
  const marker = `${userId}:${anonymousId}`;
  try {
    if (window.sessionStorage.getItem(CLAIMED_SESSION_KEY) === marker) {
      return;
    }
    window.sessionStorage.setItem(CLAIMED_SESSION_KEY, marker);
  } catch {
    // 目印を置けなくても名乗り自体は送る (サーバー側は冪等)
  }
  postTelemetryJson("/api/learning-events/claim", {
    anonymousId,
    attribution: readFirstTouch(),
  });
}

export function markSignupPending(method: string): void {
  try {
    window.localStorage.setItem(
      SIGNUP_PENDING_KEY,
      JSON.stringify({ method, at: Date.now() }),
    );
  } catch {
    // 保存できなくても登録そのものは済んでいる
  }
}

function consumeSignupPending(): { method?: string } | null {
  try {
    const raw = window.localStorage.getItem(SIGNUP_PENDING_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(SIGNUP_PENDING_KEY);
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const pending = parsed as { method?: unknown; at?: unknown };
    const at = typeof pending.at === "number" ? pending.at : 0;
    if (!at || Date.now() - at > SIGNUP_PENDING_TTL_MS) return null;
    return { method: typeof pending.method === "string" ? pending.method : undefined };
  } catch {
    return null;
  }
}

/**
 * signup が済んでから最初の「本人確認できた瞬間」に1回だけ送る。
 * セッションがあるので api-go は user_id をセッションから付ける。
 * anonymousId / sessionId は PostHog 側の行と突き合わせる鍵。
 */
function emitPendingSignup(identity: PostHogSessionIdentity | null): void {
  const pending = consumeSignupPending();
  if (!pending) return;
  postTelemetryJson("/api/learning-events", {
    eventName: "signup_completed",
    anonymousId: identity?.anonymousId,
    sessionId: identity?.sessionId,
    properties: { method: pending.method },
  });
}

/**
 * useAuth の user が確定した瞬間に呼ぶ。identify → claim →
 * 持ち越し中の signup 記録の順。全部まとめて握りつぶすので、
 * 呼び出し側は await も catch も要らない
 */
export async function syncLearningIdentity(userId: string): Promise<void> {
  try {
    const identity = await identifyPostHogUserForSession(userId);
    if (identity?.anonymousId) {
      claimAnonymousIdOnce(userId, identity.anonymousId);
    }
    emitPendingSignup(identity);
  } catch {
    // 観測用の配線がログインの UX を止めてはいけない
  }
}

/**
 * ログアウトが確かめられた瞬間に呼ぶ。ブラウザの distinct_id を
 * 新しい anonymous id に戻し、同じタブで別人が名乗れるように
 * セッションの名乗り済み目印も消す
 */
export function resetLearningIdentity(): void {
  try {
    window.sessionStorage.removeItem(CLAIMED_SESSION_KEY);
  } catch {
    // 消せなくても reset 自体は進める
  }
  resetPostHogUser();
}
